// Redux Toolkit slice for Update Profile functionality
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";

// ==================== ASYNC THUNKS ====================

// Fetch User Profile Data
export const fetchUserProfile = createAsyncThunk(
  "updateProfile/fetchUserProfile",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await agent.get(`${API.GETUSERDATA}/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch University Partners
export const fetchUniversityPartners = createAsyncThunk(
  "updateProfile/fetchUniversityPartners",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_UNIVERSITY);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Industries
export const fetchIndustries = createAsyncThunk(
  "updateProfile/fetchIndustries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_INDUSTRY);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Program Studies
export const fetchProgramStudies = createAsyncThunk(
  "updateProfile/fetchProgramStudies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.PROGRAM_STUDY_ID);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Update Profile Settings
export const fetchUpdateProfileSettings = createAsyncThunk(
  "updateProfile/fetchUpdateProfileSettings",
  async (input, { rejectWithValue }) => {
    try {
      const roleId = typeof input === "object" ? input?.roleId : input;
      const stepKey = typeof input === "object" ? input?.stepKey : "step_1";
      const payload = {
        role_type: roleId,
        step_key: stepKey || "step_1",
      };
      const response = await agent.get(API.REGISTER_SETTING, {
        params: payload,
      });
      if (response?.data?.status === 200) {
        return response.data.data;
      }
      return rejectWithValue("Failed to fetch settings");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Areas/Domains
export const fetchAreas = createAsyncThunk(
  "updateProfile/fetchAreas",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_AREAS_LIST, {});
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Objectives
export const fetchObjectives = createAsyncThunk(
  "updateProfile/fetchObjectives",
  async (roleID, { rejectWithValue }) => {
    try {
      const response = await agent.get(
        API.LOAD_ORGANIZATION_LIST_SIGNUP + `/${roleID}/objective`,
        {}
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Particular Programs
export const fetchParticularPrograms = createAsyncThunk(
  "updateProfile/fetchParticularPrograms",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.GET_PAETICULAR_PROGRAM);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Languages
export const fetchLanguages = createAsyncThunk(
  "updateProfile/fetchLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_LANGUAGE);
      const formattedData = response.data.data?.map((item) => {
        return { value: item?.title, label: item?.title };
      });
      return formattedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Experience Data
export const fetchExperience = createAsyncThunk(
  "updateProfile/fetchExperience",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.EXPERIENCE);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch All Programmes
export const fetchAllProgrammes = createAsyncThunk(
  "updateProfile/fetchAllProgrammes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_PROGRAMMES + "/all");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch Domains List
export const fetchDomainsList = createAsyncThunk(
  "updateProfile/fetchDomainsList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(API.LOAD_DOMAINS_LIST);
      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Main Update Profile Action
export const updateProfileStep = createAsyncThunk(
  "updateProfile/updateProfileStep",
  async (
    { params, step, userId, redirectPath },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await agent.post(API.REGISTER_NEW, params, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 503) {
        return rejectWithValue({
          success: false,
          message: response.data.message
        });
      }

      if (response.status === 200) {
        if (response?.data?.status === 200) {
          // Calculate redirect path based on step
          let calculatedRedirectPath = null;

          if (step === "step_1") calculatedRedirectPath = "/manage-profile-step1";
          else if (step === "step_2") calculatedRedirectPath = "/manage-profile-step2";
          else if (step === "step_3") calculatedRedirectPath = "/manage-profile-step3";
          else if (step === "step_4") calculatedRedirectPath = "/manage-profile-step4";
          else if (step === "step_5") calculatedRedirectPath = "/manage-profile";

          return {
            success: true,
            message: response.data.message,
            redirectTo: redirectPath || calculatedRedirectPath,
            data: response.data,
            step: step
          };
        }
        return rejectWithValue({
          success: false,
          message: response.data.message
        });
      }
    } catch (error) {
      return rejectWithValue({
        success: false,
        message: error.message
      });
    }
  }
);

// Delete Resume
export const deleteResume = createAsyncThunk(
  "updateProfile/deleteResume",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await agent.get(
        `${API.DELETE_RESUME}?user_id=${userId}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message
      );
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  // User Profile Data
  userProfile: null,

  // Dropdown Data
  universityPartners: [],
  industries: [],
  programStudies: [],
  areas: [],
  objectives: [],
  particularPrograms: [],
  languages: [],
  experienceData: [],
  programmes: [],
  domainsList: [],

  // Settings
  updateProfileStepFields: [],

  // Loading States
  loading: false,
  error: null,

  // Update Status
  updateStatus: "idle",
  updateMessage: "",
  updateRedirectPath: null,
};

// ==================== SLICE ====================

const updateProfileSlice = createSlice({
  name: "updateProfile",
  initialState,
  reducers: {
    resetUpdateStatus: (state) => {
      state.updateStatus = "idle";
      state.updateMessage = "";
      state.updateRedirectPath = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setUserProfile: (state, action) => {
      state.userProfile = action.payload;
    },
    clearStepFields: (state) => {
      state.updateProfileStepFields = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUserProfile
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfile = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchUniversityPartners
      .addCase(fetchUniversityPartners.fulfilled, (state, action) => {
        state.universityPartners = action.payload || [];
      })

      // fetchIndustries
      .addCase(fetchIndustries.fulfilled, (state, action) => {
        state.industries = action.payload || [];
      })

      // fetchProgramStudies
      .addCase(fetchProgramStudies.fulfilled, (state, action) => {
        state.programStudies = action.payload || [];
      })

      // fetchUpdateProfileSettings
      .addCase(fetchUpdateProfileSettings.fulfilled, (state, action) => {
        state.updateProfileStepFields = action.payload || [];
      })

      // fetchAreas
      .addCase(fetchAreas.fulfilled, (state, action) => {
        state.areas = action.payload || [];
      })

      // fetchObjectives
      .addCase(fetchObjectives.fulfilled, (state, action) => {
        state.objectives = action.payload || [];
      })

      // fetchParticularPrograms
      .addCase(fetchParticularPrograms.fulfilled, (state, action) => {
        state.particularPrograms = action.payload || [];
      })

      // fetchLanguages
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.languages = action.payload || [];
      })

      // fetchExperience
      .addCase(fetchExperience.fulfilled, (state, action) => {
        state.experienceData = action.payload || [];
      })

      // fetchAllProgrammes
      .addCase(fetchAllProgrammes.fulfilled, (state, action) => {
        state.programmes = action.payload || [];
      })

      // fetchDomainsList
      .addCase(fetchDomainsList.fulfilled, (state, action) => {
        state.domainsList = action.payload || [];
      })

      // updateProfileStep
      .addCase(updateProfileStep.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateProfileStep.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.updateMessage = action.payload.message;
        state.updateRedirectPath = action.payload.redirectTo;
      })
      .addCase(updateProfileStep.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload?.message || action.payload;
        state.updateMessage = action.payload?.message || "Update failed";
      })
      // deleteResume
      .addCase(deleteResume.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteResume.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteResume.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetUpdateStatus, clearError, setUserProfile, clearStepFields } = updateProfileSlice.actions;

export default updateProfileSlice.reducer;